import { Clock3, Minus, Plus } from "lucide-react";
import { useState } from "react";
import { timeRangeLabel, timeToMinutes } from "../utils/time.js";

function logStatusKey(log) {
  if (log?.classStatus === "suspended") return "suspended";
  if (log?.actualIn && log?.actualOut) return "complete";
  if (log?.actualIn && !log?.actualOut) return "pending";
  return "empty";
}

export function routineBoardStatus(log) {
  const key = logStatusKey(log);
  if (key === "suspended") return "Suspend";
  if (key === "complete") return "IN + OUT";
  if (key === "pending") return "Only IN";
  return "No Time";
}

export function RoutineBoardLegend() {
  return (
    <div className="routine-board-legend" aria-label="Routine board color meaning">
      <span><i className="legend-dot board-empty" /> No time data</span>
      <span><i className="legend-dot board-pending" /> Only IN time</span>
      <span><i className="legend-dot board-complete" /> IN + OUT done</span>
      <span><i className="legend-dot board-suspended" /> Suspended</span>
    </div>
  );
}

function makeSlots(routines) {
  const map = new Map();
  routines.forEach((routine) => {
    const key = `${routine.scheduledIn || ""}_${routine.scheduledOut || ""}`;
    if (!map.has(key)) {
      map.set(key, {
        key,
        inTime: routine.scheduledIn || "",
        outTime: routine.scheduledOut || "",
        label: timeRangeLabel(routine.scheduledIn, routine.scheduledOut)
      });
    }
  });
  return [...map.values()].sort((a, b) => Number(timeToMinutes(a.inTime) || 0) - Number(timeToMinutes(b.inTime) || 0));
}

function makeRows(routines) {
  const map = new Map();
  routines.forEach((routine) => {
    const key = `${routine.className || "Class"}_${routine.sectionName || "Section"}_${routine.roomNumber || "N/A"}`;
    if (!map.has(key)) {
      map.set(key, {
        key,
        title: `${routine.className || "Class"} - ${routine.sectionName || "Section"}`,
        room: routine.roomNumber || "N/A",
        routines: []
      });
    }
    map.get(key).routines.push(routine);
  });
  return [...map.values()].sort((a, b) => {
    const classCompare = a.title.localeCompare(b.title, undefined, { numeric: true });
    if (classCompare !== 0) return classCompare;
    return String(a.room).localeCompare(String(b.room), undefined, { numeric: true });
  });
}

export default function RoutineBoard({ routines = [], logs = [], onCellClick, compact = false }) {
  const [zoom, setZoom] = useState(0.86);
  const slots = makeSlots(routines);
  const rows = makeRows(routines);
  const logByRoutine = Object.fromEntries(logs.map((log) => [log.routineId, log]));
  const gridTemplateColumns = `minmax(112px, 0.9fr) repeat(${Math.max(slots.length, 1)}, minmax(136px, 1fr))`;

  return (
    <div className={`routine-board ${compact ? "routine-board-compact" : ""}`}>
      <div className="routine-board-tools">
        <span>Fit screen</span>
        <div>
          <button className="icon-button" type="button" onClick={() => setZoom((value) => Math.max(0.7, Number((value - 0.08).toFixed(2))))} aria-label="Make routine board smaller">
            <Minus size={15} />
          </button>
          <button className="icon-button" type="button" onClick={() => setZoom((value) => Math.min(1.08, Number((value + 0.08).toFixed(2))))} aria-label="Make routine board bigger">
            <Plus size={15} />
          </button>
        </div>
      </div>
      <div className="routine-board-mobile">
        {rows.map((row) => (
          <article className="routine-board-mobile-card" key={row.key}>
            <header>
              <strong>{row.title}</strong>
              <span>Room {row.room}</span>
            </header>
            <div className="routine-board-mobile-slots">
              {slots.map((slot) => {
                const cellRoutines = row.routines.filter((routine) => `${routine.scheduledIn || ""}_${routine.scheduledOut || ""}` === slot.key);
                return (
                  <div className="routine-board-mobile-slot" key={`${row.key}_${slot.key}`}>
                    <p>{slot.label}</p>
                    {cellRoutines.length ? (
                      cellRoutines.map((routine) => {
                        const log = logByRoutine[routine.id] || logByRoutine[routine.routineId];
                        const statusKey = logStatusKey(log);
                        return (
                          <button
                            className={`routine-board-cell board-${statusKey}`}
                            type="button"
                            key={routine.id || routine.routineId}
                            onClick={() => onCellClick?.(routine, log)}
                          >
                            <strong>{routine.subjectName || "Subject"}</strong>
                            <span>{routine.teacherName || "Teacher"}</span>
                            <em>{routineBoardStatus(log)}</em>
                          </button>
                        );
                      })
                    ) : (
                      <span className="routine-board-blank">-</span>
                    )}
                  </div>
                );
              })}
            </div>
          </article>
        ))}
      </div>
      <div className="routine-board-scroll">
        <div
          className="routine-board-grid"
          style={{
            "--slot-count": Math.max(slots.length, 1),
            "--board-zoom": zoom,
            gridTemplateColumns
          }}
        >
          <div className="routine-board-head routine-board-corner">Class / Room</div>
          {slots.map((slot) => (
            <div className="routine-board-head" key={slot.key}>
              <Clock3 size={14} />
              {slot.label}
            </div>
          ))}

          {rows.map((row) => (
            <div className="routine-board-row-fragment" key={row.key}>
              <div className="routine-board-row-title">
                <strong>{row.title}</strong>
                <span>Room {row.room}</span>
              </div>
              {slots.map((slot) => {
                const cellRoutines = row.routines.filter((routine) => `${routine.scheduledIn || ""}_${routine.scheduledOut || ""}` === slot.key);
                return (
                  <div className="routine-board-cell-wrap" key={`${row.key}_${slot.key}`}>
                    {cellRoutines.length ? (
                      cellRoutines.map((routine) => {
                        const log = logByRoutine[routine.id] || logByRoutine[routine.routineId];
                        const statusKey = logStatusKey(log);
                        return (
                          <button
                            className={`routine-board-cell board-${statusKey}`}
                            type="button"
                            key={routine.id || routine.routineId}
                            onClick={() => onCellClick?.(routine, log)}
                          >
                            <strong>{routine.subjectName || "Subject"}</strong>
                            <span>{routine.teacherName || "Teacher"}</span>
                            <em>{routineBoardStatus(log)}</em>
                          </button>
                        );
                      })
                    ) : (
                      <span className="routine-board-blank">-</span>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
